// src/store/games.js

import { defineStore } from "pinia";
import { http } from "./https";
import { LOCAL } from "@/constants";
import cache from '@/cache';

export const useGamesStore = defineStore("games", {
  state: () => ({
    game: {},
    userRate: 0,
    newGames: [],
    categoryGames: {},
    similarGames: [],
    voteAction: null,
    comments: [],
    messages: [],
    games: [],
    nextPage: 1,
    sliders: [],
    isGamesLoaded: false,
  }),
  getters: {
    getGame: (state) => state.game,
    getUserRate: (state) => state.userRate,
    getNewGames: (state) => state.newGames,
    getSimlarGames: (state) => state.similarGames,
    hasSimilarGames: (state) => state.similarGames.length > 0,
  },
  actions: {
    // Reset pagination for fresh load
    resetPagination() {
      this.games = [];
      this.nextPage = 1;
      this.isGamesLoaded = false;
    },
    async loadAllGames(forceRefresh = false) {
      try {
        if(this.nextPage == null){
          return;
        }
        
        const cacheKey = 'games_page_1';
        
        // For first page, try cache first
        if (this.nextPage === 1 && !forceRefresh) {
          const cachedData = cache.get(cacheKey);
          if (cachedData) {
            this.nextPage = cachedData.nextPage;
            this.games = cachedData.games;
            this.newGames = cachedData.newGames;
            this.sliders = cachedData.sliders;
            this.isGamesLoaded = true;
            return true;
          }
        }
        
        const response = await http.get(`/games?page=`+this.nextPage);
        const data = response.data;

        const previousPage = this.nextPage;
        this.nextPage = data.next_page_number;
        this.games = this.games.concat(data.games);
        this.newGames = data.new_games;
        this.sliders = data.sliders;
        this.isGamesLoaded = true;
        
        // Cache first page only
        if (previousPage === 1) {
          cache.set(cacheKey, {
            nextPage: this.nextPage,
            games: this.games,
            newGames: this.newGames,
            sliders: this.sliders,
          }, 'games');
        }

        return true;
      } catch (error) {
        console.error(error);
      }
      return false;
    },
    async fetchGame(slug) {
      try {
        // Reset game state before fetching new game to prevent stale data issues
        this.game = {};
        this.userRate = 0;
        this.similarGames = [];
        this.voteAction = null;
        this.comments = [];
        
        const selectedLocale =
          localStorage.getItem(LOCAL) || "en";
        const response = await http.get(`/game/${slug}/slug?lang=${selectedLocale}`);
        if (response.data && response.data.game) {
          this.game = response.data.game;
          this.newGames = response.data.new_games;
          this.similarGames = response.data.similarGames;
          this.voteAction = response.data.vote;
          this.userRate = response.data.userScore;
          // call fetchComments to load comments for the game
          this.fetchComments(this.game.id);
        }
      } catch (error) {
        console.error(error);
      }
    },
    async postComment(gameId, comment) {
      try {
        const response = await http.post(`/game/${gameId}/comments`, {
          comment,
        });
        if (response.data && response.data.comment) {
          // Push at the beginning of the array
          this.comments.unshift(response.data.comment);
          return true;
        }
      } catch (error) {
        console.error(error);
      }
      return false;
    },
    async rateGame(gameId, rate) {
      try {
        const response = await http.post(`/game/${gameId}/rate`, {
          rate,
        });
        if (response.data && response.data.status) {
          this.game = response.data.game;
          this.userRate = rate;
          return true;
        }
      } catch (error) {
        console.error(error);
      }
      return false;
    },
    // Load comments for the game
    async fetchComments(gameId) {
      try {
        const response = await http.get(`/game/${gameId}/comments`);
        if (response.data && response.data.comments) {
          this.comments = response.data.comments;
        }
      } catch (error) {
        console.error(error);
      }
    },
    async voteGame(id, action) {
      try {
        let response = await http.get(`/vote/${id}/${action}`);

        if (this.voteAction == action) {
          this.voteAction = null;
        } else {
          this.voteAction = action;
        }
        return response.data;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    async postMessage(message, gameId) {
      try {
        let request = {
          message: message,
        };
        if (gameId) {
          request["game_id"] = gameId;
        }
        const response = await http.post(`/send-message`, request);
        if (response.data && response.data.message) {
          const todayKey = 'Today';
          const newMessage = response.data.message;
          if (this.messages[todayKey]) {
            const todayMessages = this.messages[todayKey];
            const lastBlock = todayMessages[todayMessages.length - 1];
            if (lastBlock && lastBlock.client_id === newMessage.client.id) {
              lastBlock.messages.unshift(newMessage);
            } else {
              todayMessages.push({
                client_id: newMessage.client.id,
                messages: [newMessage],
              });
            }
          } else {
            this.$set(this.messages, todayKey, [
              {
                client_id: newMessage.client.id,
                messages: [newMessage],
              },
            ]);
          }
          return true;
        }
      } catch (error) {
        console.error(error);
      }
      return false;
    },
    async fetchMessages(gameId) {
      try {
        let url = gameId ? `/get-messages/${gameId}`: `/get-messages`;
        const response = await http.get(url);
        const result = response.data;
        if (result && result.data) {
          this.messages = result.data;
        }

        return true;
      } catch (error) {
        console.error(error);
      }
      return false;
    },
    async toggleFavorites(gameId) {
      try {
        const response = await http.get(`/game/${gameId}/favorites/`);
        const result = response.data;
        // {"responseMessage":"Game added to favorites","status":true,"responseCode":1}
        if (result && result.status) {
          return result.responseMessage;
        }
      } catch (error) {
        console.error(error);
      }
      return false;
    },
  },
});
